import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

export interface TenantDetail {
  id: string;
  slug: string;
  name: string;
  legal_name: string | null;
  admin_domain: string | null;
  support_email: string | null;
  support_whatsapp: string | null;
  legal_cnpj: string | null;
  billing_email: string | null;
  // KLUB-185 — o cadastro jurídico do clube passou a ser lido e editado aqui.
  // Antes só a aba Cobrança do admin do clube os mostrava, e a RLS já recusava
  // o staff que tentasse gravá-los.
  legal_address: string | null;
  legal_jurisdiction: string | null;
  privacy_contact: string | null;
  locale: string;
  timezone: string;
  currency: string;
  lifecycle_state: string;
  created_at: string;
  // KLUB-245 — the minimum code version every device of this club must run.
  // `null` = no minimum; written only through platform-set-min-app-code-version.
  min_app_code_version: number | null;
}

const TENANT_COLUMNS =
  "id, slug, name, legal_name, admin_domain, support_email, support_whatsapp, legal_cnpj, billing_email, legal_address, legal_jurisdiction, privacy_contact, locale, timezone, currency, lifecycle_state, created_at, min_app_code_version";

export interface LifecycleEvent {
  id: string;
  from_state: string | null;
  to_state: string;
  triggered_by: string;
  reason: string | null;
  created_at: string;
}

export function useTenant(slug: string) {
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [events, setEvents] = useState<LifecycleEvent[]>([]);
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [reloadKey, setReloadKey] = useState(0);
  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const { data: t, error: tErr } = await supabase
        .from("tenants")
        .select(TENANT_COLUMNS)
        .eq("slug", slug)
        .maybeSingle();
      if (cancelled) return;
      if (tErr || !t) {
        setError(tErr?.message ?? "tenant not found");
        setLoading(false);
        return;
      }
      setTenant(t as TenantDetail);

      // Members are profiles with role='member' for this tenant.
      const [{ data: ev }, { count }] = await Promise.all([
        supabase
          .from("tenant_lifecycle_events")
          .select("id, from_state, to_state, triggered_by, reason, created_at")
          .eq("tenant_id", (t as { id: string }).id)
          .order("created_at", { ascending: false }),
        supabase
          .from("profiles")
          .select("*", { count: "exact", head: true })
          .eq("tenant_id", (t as { id: string }).id)
          .eq("role", "member"),
      ]);
      if (cancelled) return;
      setEvents((ev ?? []) as LifecycleEvent[]);
      setMemberCount(count);
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [slug, reloadKey]);

  return { tenant, events, memberCount, loading, error, refetch };
}
